import { api, fetchWithAuth } from '@/lib/api'
import {
  InvoiceFilters,
  InvoiceStatus,
  InvoicePart,
  InvoiceService,
  WebInvoice,
  InvoicePayment,
  InvoicePaymentsListResponse,
  InvoiceDisplayStatus,
} from '@/lib/types'
import {
  toDisplayStatus,
  type InvoiceReminderResult,
  type InvoiceReportPeriod,
  type InvoiceReportSummary,
} from '@tenfour/contracts'

function mapPayment(raw: any): InvoicePayment {
  return {
    id: raw.id,
    invoiceId: raw.invoiceId ?? '',
    accountId: raw.accountId ?? raw.contractorId ?? '',
    kind: raw.kind,
    method: raw.method,
    status: raw.status,
    amount: Number(raw.amount ?? 0),
    paidAt: raw.paidAt,
    stripePaymentIntentId: raw.stripePaymentIntentId ?? null,
    createdAt: raw.createdAt,
  }
}

function mapInvoice(raw: any): WebInvoice {
  const lineItems: any[] = raw.lineItems ?? []
  const parts: InvoicePart[] = lineItems
    .filter((li: any) => li.type === 'part')
    .map((li: any) => ({ name: li.name, quantity: Number(li.quantity), price: Number(li.unitPrice) }))
  const services: InvoiceService[] = lineItems
    .filter((li: any) => li.type === 'service')
    .map((li: any) => ({
      name: li.name,
      description: li.description ?? li.name,
      quantity: Number(li.quantity),
      price: Number(li.unitPrice),
      taxable: li.taxable,
    }))

  const payments: InvoicePayment[] = (raw.payments ?? []).map(mapPayment)
  const paidAmount = raw.paidAmount != null ? Number(raw.paidAmount) : undefined
  const balanceDue = raw.balanceDue != null ? Number(raw.balanceDue) : undefined
  const isFullyPaid = raw.isFullyPaid ?? false
  const status = raw.status as InvoiceStatus

  const displayStatus: InvoiceDisplayStatus =
    raw.displayStatus ??
    toDisplayStatus({
      status,
      isFullyPaid,
      overdueAt: raw.overdueAt,
    })

  // Joined customer/job details (invoice detail only) → BILL TO / JOB ADDRESS.
  const c = (raw as {
    customer?: {
      customerNumber?: number | null
      email?: string | null
      street?: string | null
      city?: string | null
      state?: string | null
      zip?: string | null
    }
  }).customer
  const j = (raw as {
    job?: { serviceStreet?: string | null; serviceCity?: string | null; serviceState?: string | null; serviceZip?: string | null }
  }).job
  const cityStateZip = (city?: string | null, state?: string | null, zip?: string | null) => {
    const sz = [state, zip].filter(Boolean).join(' ')
    return [city, sz].filter(Boolean).join(', ') || null
  }
  const hasJobAddress = !!(j?.serviceStreet || j?.serviceCity || j?.serviceState || j?.serviceZip)

  return {
    id: raw.id,
    invoiceNumber: raw.invoiceNumber,
    accountId: raw.accountId,
    customerId: raw.customerId ?? undefined,
    jobId: raw.jobId ?? undefined,
    customerName: raw.customerName,
    customerPhone: raw.customerPhone ?? undefined,
    customerAddress: raw.customerAddress ?? undefined,
    templateId: raw.templateId ?? '',
    memo: raw.memo ?? undefined,
    footerOverride: raw.footerOverride ?? undefined,
    overdueAt: raw.overdueAt ?? undefined,
    laborHours: Number(raw.laborHours ?? 0),
    laborRate: Number(raw.laborRate ?? 0),
    laborTotal: Number(raw.laborTotal ?? 0),
    lineItems,
    parts,
    partsSubtotal: Number(raw.partsTotal ?? raw.partsSubtotal ?? 0),
    services,
    servicesSubtotal: Number(raw.servicesTotal ?? raw.servicesSubtotal ?? 0),
    taxRate: Number(raw.taxRate ?? 0),
    taxableAmount: Number(raw.taxableAmount ?? 0),
    taxAmount: Number(raw.taxAmount ?? 0),
    taxState: raw.taxState ?? null,
    taxZip: j?.serviceZip ?? c?.zip ?? null,
    taxWarning: raw.taxWarning ?? null,
    taxBasis: raw.taxBasis ?? null,
    reviewFlaggedAt: raw.reviewFlaggedAt ?? null,
    reviewRejectionReason: raw.reviewRejectionReason ?? null,
    // BILL TO / JOB ADDRESS block + meta (invoice detail; mirror the PDF).
    //
    // `undefined` and `null` are NOT the same here and the paper preview relies
    // on the difference: `GET /invoices` (the list) joins neither customer nor
    // job, so the workspace preview pane renders from a payload where these
    // were never fetched. Collapsing that to `null` made the preview print an
    // em dash for every customer id and job address — data that exists, shown
    // as absent. `undefined` = not loaded (skip the field), `null` = loaded and
    // genuinely empty (show the em dash, like the PDF does).
    customerNumber: c ? c.customerNumber ?? null : undefined,
    invoiceDate: raw.invoiceDate ?? undefined,
    billStreet: c ? c.street ?? null : undefined,
    billCityStateZip: c ? cityStateZip(c.city, c.state, c.zip) : undefined,
    billEmail: c ? c.email ?? null : undefined,
    // JOB ADDRESS defaults to the customer's billing address when the job has
    // none — mirrors the worker's #209 tax fallback (job zip → customer zip), so
    // the contractor SEES where tax came from instead of a blank "—" beside a
    // tax line. A job with its own service address still wins (different-property
    // override). Fall back wholesale (not field-by-field) so a job's partial
    // address never mixes with the customer's into a Frankenstein line.
    jobStreet: !j && !c ? undefined : hasJobAddress ? (j?.serviceStreet ?? null) : (c?.street ?? null),
    jobCityStateZip:
      !j && !c
        ? undefined
        : hasJobAddress
          ? cityStateZip(j?.serviceCity, j?.serviceState, j?.serviceZip)
          : cityStateZip(c?.city, c?.state, c?.zip),
    jobAddressIsBilling: !hasJobAddress && !!(c?.street || c?.city || c?.state || c?.zip),
    discountName: raw.discountName ?? undefined,
    discountKind: raw.discountKind === 'fixed' ? 'fixed' : 'percent',
    discountPercent: Number(raw.discountPercent ?? 0),
    discountAmount: Number(raw.discountAmount ?? 0),
    total: Number(raw.total ?? 0),
    status,
    displayStatus,
    paidAmount,
    balanceDue,
    isFullyPaid,
    payments,
    // one column, one meaning — when the customer was last texted a
    // payment reminder by anyone. The UI renders "reminded 3h ago" from it; it
    // no longer disables anything, because the manual send is never blocked.
    lastReminderSentAt: raw.lastReminderSentAt ?? null,
    paymentType: raw.paymentType ?? 'TEXT_ONLY',
    createdAt: raw.createdAt,
    sentAt: raw.sentAt ?? undefined,
    paidAt: raw.paidAt ?? undefined,
    dueDate: raw.dueDate ?? undefined,
    dueTimezone: raw.dueTimezone ?? 'UTC',
    pdfUrl: raw.pdfUrl ?? undefined,
    viewCount: raw.viewCount ?? 0,
    stripePaymentLinkId: raw.stripePaymentLinkId ?? undefined,
    stripePaymentLinkUrl: raw.stripePaymentLinkUrl ?? undefined,
  }
}

export async function listInvoices(
  accountId: string,
  filters?: InvoiceFilters & { cursor?: string },
): Promise<{ invoices: WebInvoice[]; hasMore: boolean; nextCursor: string | null }> {
  const params = new URLSearchParams({ accountId })
  if (filters?.status) params.set('status', filters.status)
  if (filters?.state) params.set('state', filters.state)
  if (filters?.customerId) params.set('customerId', filters.customerId)
  if (filters?.limit) params.set('limit', String(filters.limit))
  if (filters?.cursor) params.set('cursor', filters.cursor)

  const raw = await api<any>(`/invoices?${params}`)
  return {
    invoices: (raw.invoices ?? []).map(mapInvoice),
    hasMore: raw.hasMore ?? false,
    nextCursor: raw.nextCursor ?? null,
  }
}

/**
 * /reports figures — `GET /invoices/report-summary` (#574): aggregated server-
 * side over EVERY paid invoice, keyed on when each was paid. Requires the
 * per-user View reports permission (#572) — a 403 here means the caller lacks it.
 */
export async function getInvoiceReportSummary(
  accountId: string,
  period: InvoiceReportPeriod,
): Promise<InvoiceReportSummary> {
  const params = new URLSearchParams({ accountId, period })
  return api<InvoiceReportSummary>(`/invoices/report-summary?${params}`)
}

/** The /reports Payments CSV — served by the same gated aggregate (`?format=csv`), complete at any size. */
export async function downloadInvoiceReportCsv(accountId: string, period: InvoiceReportPeriod): Promise<Blob> {
  const params = new URLSearchParams({ accountId, period, format: 'csv' })
  const res = await fetchWithAuth(`/invoices/report-summary?${params}`)
  if (!res.ok) throw new Error(String(res.status))
  return res.blob()
}

export async function listAllInvoices(accountId: string): Promise<WebInvoice[]> {
  const all: WebInvoice[] = []
  let cursor: string | null = null

  for (let page = 0; page < 25; page += 1) {
    const result = await listInvoices(accountId, { limit: 200, cursor: cursor ?? undefined })
    all.push(...result.invoices)
    if (!result.hasMore || !result.nextCursor) break
    cursor = result.nextCursor
  }

  return all
}

/**
 * Account-wide payments-received list (the /payouts page) — every
 * invoice_payments ledger row, newest first, cursor-paginated. Amounts are
 * dollars (unlike ConnectFinances' minor units).
 */
export async function getPaymentsReceived(
  accountId: string,
  opts?: { limit?: number; cursor?: string | null },
): Promise<InvoicePaymentsListResponse> {
  const params = new URLSearchParams({ accountId })
  if (opts?.limit) params.set('limit', String(opts.limit))
  if (opts?.cursor) params.set('cursor', opts.cursor)
  return api<InvoicePaymentsListResponse>(`/invoices/payments?${params}`)
}

export async function getInvoice(invoiceId: string): Promise<WebInvoice> {
  const raw = await api<any>(`/invoices/${invoiceId}`)
  return mapInvoice(raw)
}

export async function createInvoice(input: {
  accountId: string
  customerId: string
  jobId?: string
  laborHours?: number
  laborRate?: number
  lineItems?: Array<{
    type: 'labor' | 'part' | 'service'
    name: string
    description?: string
    quantity?: number
    unitPrice: number
    taxable?: boolean
  }>
  discountName?: string
  discountPercent?: number
  memo?: string
  footerOverride?: string
  dueDate?: string
  dueTimezone?: string
  asQuote?: boolean
}): Promise<WebInvoice> {
  const raw = await api<any>('/invoices', {
    method: 'POST',
    body: JSON.stringify(input),
  })
  return mapInvoice(raw)
}

/**
 * One-shot "Generate Invoice" (#358/#359): reuse the REAL Aria AI path
 * (`/assistant/conversations/:id/stream`, invoice-mode + write_invoice) instead
 * of a bespoke endpoint. Opens a throwaway conversation, sends the contractor's
 * instruction plus the launching job (so the invoice binds to the job's address
 * + location tax, backend-injected), consumes the SSE stream, and resolves the
 * created invoice id. `onStatus` streams a human-readable status for the button.
 */
export async function generateInvoiceFromInstruction(input: {
  instruction: string
  jobId: string
  customerName?: string
  onStatus?: (status: string) => void
}): Promise<{ invoiceId: string | null; assistantReply: string | null }> {
  const convo = await api<{ id: string }>('/assistant/conversations', {
    method: 'POST',
    body: JSON.stringify({ title: 'Generate invoice' }),
  })

  // Name the customer up front so Aria proceeds straight to write_invoice
  // instead of stopping to ask "who is this for?" — the instruction is the job
  // description and names no customer. The backend still binds the job's real
  // customerId + jobId to the create, so the name is guidance, not the key.
  const message = input.customerName?.trim()
    ? `Create an invoice for ${input.customerName.trim()}. ${input.instruction}`
    : input.instruction

  // Safety net: the assistant runs a tool loop synchronously, so a stuck run
  // would otherwise leave the caller's button spinning forever. Abort after 2
  // minutes (comfortably above a normal multi-turn generate) with a clear error.
  const abort = new AbortController()
  const timeout = setTimeout(() => abort.abort(), 120_000)

  let res: Response
  try {
    res = await fetchWithAuth(`/assistant/conversations/${convo.id}/stream`, {
      method: 'POST',
      body: JSON.stringify({ message, jobId: input.jobId }),
      signal: abort.signal,
    })
  } catch (err) {
    clearTimeout(timeout)
    if (abort.signal.aborted) {
      throw new Error('That took too long — try again, or add the line items by hand.')
    }
    throw err
  }
  if (!res.ok || !res.body) {
    clearTimeout(timeout)
    throw new Error(`The assistant is unavailable right now (${res.status}). Try again in a moment.`)
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buf = ''
  let invoiceId: string | null = null
  let streamError: string | null = null
  let assistantReply: string | null = null

  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
    buf += decoder.decode(value, { stream: true })
    let sep = buf.indexOf('\n\n')
    while (sep !== -1) {
      const frame = buf.slice(0, sep)
      buf = buf.slice(sep + 2)
      const dataLines: string[] = []
      for (const line of frame.split('\n')) {
        if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''))
      }
      if (dataLines.length > 0) {
        try {
          const evt = JSON.parse(dataLines.join('\n')) as {
            kind?: string
            name?: string
            invoiceId?: string
            message?: string
            finalReply?: string
          }
          if (evt.kind === 'tool_call' && evt.name === 'write_invoice') {
            input.onStatus?.('Drafting the invoice…')
          } else if (evt.kind === 'tool_result' && evt.name === 'write_invoice' && typeof evt.invoiceId === 'string') {
            invoiceId = evt.invoiceId
            input.onStatus?.('Invoice created — opening it…')
          } else if (evt.kind === 'done' && typeof evt.finalReply === 'string' && evt.finalReply.trim()) {
            // The assistant ended with plain text (a question or a refusal) and no
            // invoice — capture it so the caller can show it instead of nothing (#469).
            assistantReply = evt.finalReply.trim()
          } else if (evt.kind === 'error') {
            streamError = evt.message || 'The assistant hit an error.'
          }
        } catch {
          /* ignore a malformed frame */
        }
      }
        sep = buf.indexOf('\n\n')
      }
    }
  } finally {
    clearTimeout(timeout)
  }

  if (invoiceId) return { invoiceId, assistantReply: null }
  // A real stream error is still an error the caller should toast.
  if (streamError) throw new Error(streamError)
  // No invoice, but the assistant said something (a clarifying question or a
  // business-rule refusal) — hand it back so the caller can display it (#469).
  if (assistantReply) return { invoiceId: null, assistantReply }
  throw new Error(
    "tenfour couldn't create the invoice from that — try describing what to bill (items + amounts).",
  )
}

// Alias kept for existing page imports
export const getInvoiceDetail = getInvoice

export async function updateInvoice(
  invoiceId: string,
  updates: {
    /** Reassign the invoice to another customer (pre-send statuses only —
     *  the API re-denormalizes name/phone/address server-side). */
    customerId?: string
    /** Link or unlink a job owned by this invoice's account and customer. */
    jobId?: string | null
    parts?: Array<{ name: string; quantity: number; price: number }>
    services?: Array<{ name: string; description: string; quantity: number; price: number; taxable: boolean }>
    lineItems?: Array<{
      type: 'labor' | 'part' | 'service'
      name: string
      description?: string
      quantity: number
      unitPrice: number
      taxable?: boolean
    }>
    discountPercent?: number
    discountAmount?: number
    discountName?: string
    dueDate?: string
    dueTimezone?: string
    memo?: string
  },
): Promise<void> {
  const body: any = {}

  if (updates.lineItems !== undefined) {
    body.lineItems = updates.lineItems
  } else if (updates.parts !== undefined || updates.services !== undefined) {
    const partItems = (updates.parts ?? []).map((p) => ({
      type: 'part',
      name: p.name,
      quantity: p.quantity,
      unitPrice: p.price,
      taxable: true,
    }))
    const serviceItems = (updates.services ?? []).map((s) => ({
      type: 'service',
      name: s.name || s.description,
      description: s.description,
      quantity: s.quantity,
      unitPrice: s.price,
      taxable: s.taxable,
    }))
    body.lineItems = [...partItems, ...serviceItems]
  }

  if (updates.customerId !== undefined) body.customerId = updates.customerId
  if (updates.jobId !== undefined) body.jobId = updates.jobId
  if (updates.discountPercent !== undefined) body.discountPercent = updates.discountPercent
  if (updates.discountAmount !== undefined) body.discountAmount = updates.discountAmount
  if (updates.discountName !== undefined) body.discountName = updates.discountName
  if (updates.dueDate !== undefined) body.dueDate = updates.dueDate
  if (updates.dueTimezone !== undefined) body.dueTimezone = updates.dueTimezone
  if (updates.memo !== undefined) body.memo = updates.memo

  await api(`/invoices/${invoiceId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  })
}

export async function updateInvoiceStatus(
  invoiceId: string,
  status: InvoiceStatus,
  paymentMethod?: 'CASH' | 'CHECK' | 'STRIPE' | 'VENMO',
): Promise<void> {
  await api(`/invoices/${invoiceId}`, {
    method: 'PATCH',
    body: JSON.stringify({ status, paymentType: paymentMethod }),
  })
}

/** Approve and send a draft through the PDF, payment-link, and customer-text pipeline. */
export async function approveInvoice(invoiceId: string): Promise<void> {
  await api(`/invoices/${invoiceId}/approve`, { method: 'POST' })
}

// Un-approve (toggle back to DRAFT) — removes it from the ops ready-to-send list.
export async function unapproveInvoice(invoiceId: string): Promise<void> {
  await api(`/invoices/${invoiceId}/unapprove`, { method: 'POST' })
}

// Record a manual (cash/check/Venmo/Cash App) payment against a sent invoice.
export type ManualPaymentMethod = 'CASH' | 'CHECK' | 'VENMO' | 'CASH_APP'
export async function recordInvoicePayment(
  invoiceId: string,
  body: { method: ManualPaymentMethod; amount?: number },
): Promise<void> {
  await api(`/invoices/${invoiceId}/payment`, { method: 'POST', body: JSON.stringify(body) })
}

// Edit a recorded manual payment (correct amount/method).
export async function updateInvoicePayment(
  invoiceId: string,
  paymentId: string,
  body: { method: ManualPaymentMethod; amount?: number },
): Promise<void> {
  await api(`/invoices/${invoiceId}/payment/${paymentId}`, { method: 'PATCH', body: JSON.stringify(body) })
}

// Delete a manual payment recorded in error.
export async function deleteInvoicePayment(invoiceId: string, paymentId: string): Promise<void> {
  await api(`/invoices/${invoiceId}/payment/${paymentId}`, { method: 'DELETE' })
}

// Delete was removed — cancel (the only void path) preserves history.
export async function resendInvoice(invoiceId: string): Promise<void> {
  await api(`/invoices/${invoiceId}/resend`, { method: 'POST' })
}

/** `{ queued: false, debounced: true }` only for a repeat press inside 60s — never for the 24h cooldown. */
export async function sendInvoiceReminder(invoiceId: string): Promise<InvoiceReminderResult> {
  return api<InvoiceReminderResult>(`/invoices/${invoiceId}/reminder`, { method: 'POST' })
}

/** Cancel an invoice — the dedicated /deny endpoint (NOT a PATCH status). It's the
 *  one void path: it emits invoice.voided (expiring the Stripe session) and carries
 *  the status-aware cancel gate. PATCH is content-only now (#464). */
export async function voidInvoice(invoiceId: string): Promise<void> {
  await api(`/invoices/${invoiceId}/deny`, { method: 'POST' })
}

/** Restore a cancelled invoice back to DRAFT (uncancel). */
export async function uncancelInvoice(invoiceId: string): Promise<void> {
  await api(`/invoices/${invoiceId}/uncancel`, { method: 'POST' })
}

/** Promote a QUOTE to a billable DRAFT. */
export async function convertToInvoice(invoiceId: string): Promise<WebInvoice> {
  const raw = await api<any>(`/invoices/${invoiceId}/convert-to-invoice`, { method: 'POST' })
  return mapInvoice(raw)
}

/** Send a quote straight to the customer as a PDF over text (no ops review). */
export async function sendQuote(invoiceId: string): Promise<{ sent: boolean; invoiceId: string }> {
  return api(`/invoices/${invoiceId}/send-quote`, { method: 'POST' })
}

/**
 * Authorize an in-browser voice call with Aria. Returns the voice-agent
 * WebSocket URL plus the Twilio-style customParameters to stamp into the
 * stream's `start` frame (see @/lib/useVoiceCall). Passing an invoiceId primes
 * Aria with that invoice's context.
 */
export async function createVoiceSession(
  invoiceId?: string,
): Promise<{ wssUrl: string; parameters: Record<string, string> }> {
  return api('/voice/session', {
    method: 'POST',
    body: JSON.stringify(invoiceId ? { invoiceId } : {}),
  })
}

export function buildInvoicesCsv(invoices: WebInvoice[]): string {
  // #173: include tax jurisdiction columns for sales-tax accounting / state filing.
  // #231: the "Tax On Invoice" column was dropped — pass/absorb was removed
  // (#206), so it could only ever read "shown" and carried no information.
  const header =
    'Invoice #,Customer,Phone,Tax State,Subtotal,Taxable Amount,Tax Amount,Total,Status,Display Status,Due,Created'
  const rows = invoices.map((inv) => {
    const subtotal =
      (inv.partsSubtotal ?? 0) + (inv.servicesSubtotal ?? 0) + (inv.laborTotal ?? 0)
    return [
      inv.invoiceNumber,
      inv.customerName,
      inv.customerPhone ?? '',
      inv.taxState ?? '',
      subtotal.toFixed(2),
      (inv.taxableAmount ?? 0).toFixed(2),
      inv.taxAmount.toFixed(2),
      inv.total,
      inv.status,
      inv.displayStatus ?? '',
      inv.dueDate ?? inv.overdueAt ?? '',
      inv.createdAt,
    ]
      .map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`)
      .join(',')
  })
  return [header, ...rows].join('\n')
}
